<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);
const selectedTeamIds = ref([]);
const selectedInboxIds = ref([]);
const configurableTeamIds = ref([]);
const configurableInboxIds = ref([]);

const nameInput = ref(null);
const teamsList = computed(() => store.getters['teams/getTeams']);
const inboxesList = computed(() => store.getters['inboxes/getInboxes']);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  selectedTeamIds.value = [];
  selectedInboxIds.value = [];
  configurableTeamIds.value = [];
  configurableInboxIds.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
  selectedTeamIds.value = props.selectedRole.allowed_team_ids || [];
  selectedInboxIds.value = props.selectedRole.allowed_inbox_ids || [];
  configurableTeamIds.value = props.selectedRole.configurable_team_ids || [];
  configurableInboxIds.value = props.selectedRole.configurable_inbox_ids || [];
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

watch(selectedTeamIds, newTeamIds => {
  // Remove configurable team IDs that are no longer selected
  configurableTeamIds.value = configurableTeamIds.value.filter(id =>
    newTeamIds.includes(id)
  );
});

watch(selectedInboxIds, newInboxIds => {
  // Remove configurable inbox IDs that are no longer selected
  configurableInboxIds.value = configurableInboxIds.value.filter(id =>
    newInboxIds.includes(id)
  );
});

onMounted(async () => {
  // Load teams and inboxes
  await Promise.all([
    store.dispatch('teams/get'),
    store.dispatch('inboxes/get'),
  ]);

  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
      allowed_team_ids: selectedPermissions.value.includes(
        'settings_teams_manage'
      )
        ? selectedTeamIds.value
        : [],
      allowed_inbox_ids: selectedPermissions.value.includes(
        'settings_inboxes_manage'
      )
        ? selectedInboxIds.value
        : [],
      configurable_team_ids: selectedPermissions.value.includes(
        'settings_teams_manage'
      )
        ? configurableTeamIds.value
        : [],
      configurable_inbox_ids: selectedPermissions.value.includes(
        'settings_inboxes_manage'
      )
        ? configurableInboxIds.value
        : [],
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);

const generalPermissions = computed(() => {
  return AVAILABLE_CUSTOM_ROLE_PERMISSIONS.filter(
    p => !p.startsWith('settings_')
  );
});

const settingsPermissions = computed(() => {
  return AVAILABLE_CUSTOM_ROLE_PERMISSIONS.filter(p =>
    p.startsWith('settings_')
  );
});

const showTeamsSelection = computed(() => {
  return selectedPermissions.value.includes('settings_teams_manage');
});

const showInboxesSelection = computed(() => {
  return selectedPermissions.value.includes('settings_inboxes_manage');
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="permission in generalPermissions"
            :key="permission"
            class="flex items-center"
          >
            <input
              :id="permission"
              v-model="selectedPermissions"
              type="checkbox"
              :value="permission"
              name="permissions"
              class="ltr:mr-2 rtl:ml-2"
            />
            <label :for="permission" class="text-sm font-normal">
              {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
            </label>
          </div>
        </div>
      </div>

      <div class="w-full">
        <label class="text-sm font-semibold mb-2 block">
          {{ $t('CUSTOM_ROLE.FORM.SETTINGS_PERMISSIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="permission in settingsPermissions"
            :key="permission"
            class="flex flex-col"
          >
            <div class="flex items-center">
              <input
                :id="permission"
                v-model="selectedPermissions"
                type="checkbox"
                :value="permission"
                name="settings_permissions"
                class="ltr:mr-2 rtl:ml-2"
              />
              <label :for="permission" class="text-sm font-normal">
                {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
              </label>
            </div>
            <div
              v-if="
                permission === 'settings_teams_manage' && showTeamsSelection
              "
              class="ml-6 mt-2 flex flex-col gap-2 max-h-40 overflow-y-auto"
            >
              <div
                v-for="team in teamsList"
                :key="team.id"
                class="flex items-center gap-4"
              >
                <div class="flex items-center">
                  <input
                    :id="`team-${team.id}`"
                    v-model="selectedTeamIds"
                    type="checkbox"
                    :value="team.id"
                    class="ltr:mr-2 rtl:ml-2"
                  />
                  <label :for="`team-${team.id}`" class="text-sm font-normal">
                    {{ team.name }}
                  </label>
                </div>
                <div
                  v-if="selectedTeamIds.includes(team.id)"
                  class="flex items-center"
                >
                  <input
                    :id="`team-config-${team.id}`"
                    v-model="configurableTeamIds"
                    type="checkbox"
                    :value="team.id"
                    class="ltr:mr-2 rtl:ml-2"
                  />
                  <label
                    :for="`team-config-${team.id}`"
                    class="text-sm font-normal text-slate-600"
                  >
                    {{ $t('CUSTOM_ROLE.FORM.CAN_CONFIGURE') }}
                  </label>
                </div>
              </div>
            </div>
            <div
              v-if="
                permission === 'settings_inboxes_manage' && showInboxesSelection
              "
              class="ml-6 mt-2 flex flex-col gap-2 max-h-40 overflow-y-auto"
            >
              <div
                v-for="inbox in inboxesList"
                :key="inbox.id"
                class="flex items-center gap-4"
              >
                <div class="flex items-center">
                  <input
                    :id="`inbox-${inbox.id}`"
                    v-model="selectedInboxIds"
                    type="checkbox"
                    :value="inbox.id"
                    class="ltr:mr-2 rtl:ml-2"
                  />
                  <label :for="`inbox-${inbox.id}`" class="text-sm font-normal">
                    {{ inbox.name }}
                  </label>
                </div>
                <div
                  v-if="selectedInboxIds.includes(inbox.id)"
                  class="flex items-center"
                >
                  <input
                    :id="`inbox-config-${inbox.id}`"
                    v-model="configurableInboxIds"
                    type="checkbox"
                    :value="inbox.id"
                    class="ltr:mr-2 rtl:ml-2"
                  />
                  <label
                    :for="`inbox-config-${inbox.id}`"
                    class="text-sm font-normal text-slate-600"
                  >
                    {{ $t('CUSTOM_ROLE.FORM.CAN_CONFIGURE') }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
